// src/services/errorLoggingService.js
class ErrorLoggingService {
    constructor() {
        this.errors = [];
        this.maxErrors = 50;
        this.sessionId = this.generateSessionId();
        this.loadStoredErrors();
    }

    generateSessionId() {
        return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    loadStoredErrors() {
        try {
            const stored = localStorage.getItem('error_logs');
            if (stored) {
                this.errors = JSON.parse(stored);
            }
        } catch (err) {
            console.error('Failed to load stored errors:', err);
        }
    }

    storeErrors() {
        try {
            localStorage.setItem('error_logs', JSON.stringify(this.errors.slice(0, this.maxErrors)));
        } catch (err) {
            console.error('Failed to store errors:', err);
        }
    }

    async logError(error, context = {}) {
        const errorObj = {
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
            message: error.message || error.toString(),
            stack: error.stack,
            source: context.source || 'unknown',
            line: context.line || 0,
            column: context.column || 0,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            time: new Date().toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            }),
            sessionId: this.sessionId,
            context: {
                userEmail: context.userEmail || null,
                component: context.component || null,
                action: context.action || null,
                additionalData: context.additionalData || null,
            },
        };

        // Store locally
        this.errors.unshift(errorObj);
        if (this.errors.length > this.maxErrors) {
            this.errors.pop();
        }

        // Store in localStorage
        this.storeErrors();

        // Optional: Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.group('🔴 Error Logged');
            console.error('Message:', errorObj.message);
            console.error('Source:', errorObj.source);
            console.error('Time:', errorObj.time);
            console.groupEnd();
        }

        return errorObj;
    }

    getRecentErrors(limit = 10) {
        return this.errors.slice(0, limit);
    }

    clearErrors() {
        this.errors = [];
        this.storeErrors();
    }

    setupGlobalErrorHandling(userEmail = null) {
        // Handle window errors
        const errorHandler = async (event) => {
            await this.logError(
                new Error(event.message),
                {
                    source: event.filename?.split('/').pop() || 'unknown',
                    line: event.lineno,
                    column: event.colno,
                    userEmail,
                }
            );
        };

        // Handle unhandled promise rejections
        const rejectionHandler = async (event) => {
            await this.logError(
                event.reason || new Error('Unhandled Promise Rejection'),
                {
                    source: 'Promise',
                    userEmail,
                }
            );
        };

        window.addEventListener('error', errorHandler);
        window.addEventListener('unhandledrejection', rejectionHandler);

        // Return cleanup function
        return () => {
            window.removeEventListener('error', errorHandler);
            window.removeEventListener('unhandledrejection', rejectionHandler);
        };
    }
}

export default new ErrorLoggingService();